import React, { useEffect, useRef } from 'react'
import { Image, Animated, Platform } from 'react-native'
import Svg, { Path, Rect } from 'react-native-svg'
import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '@fliisi/i18n'
import theme from '../theme'
import {
  Button, ButtonText, LanguageSwitcher, Text, YStack, ScrollView,
  XStack,
} from '@fliisi/pollen'

/** Ornamental flourish divider */
const Flourish = ({ color = theme.colors.brand.primary }) => (
  <Svg width={340} height={40} viewBox="0 0 340 40" style={{ alignSelf: 'center' }}>
    <Path d="M10 20 L130 20 M210 20 L330 20" stroke={color} strokeWidth={0.6} />
    <Path
      d="M130 20 C140 20 145 10 155 10 C165 10 165 30 170 30 C175 30 175 10 185 10 C195 10 200 20 210 20"
      fill="none" stroke={color} strokeWidth={1}
    />
    <Path
      d="M130 20 C140 20 145 30 155 30 C165 30 165 10 170 10 C175 10 175 30 185 30 C195 30 200 20 210 20"
      fill="none" stroke={color} strokeWidth={1} opacity={0.3}
    />
    <Rect x={167} y={17} width={6} height={6} transform="rotate(45 170 20)" fill={color} />
  </Svg>
)

const fliisiLogo = require('../../assets/fliisi-logo.png')

const VALUE_PROPS = [
  { fi: 'Selaa netissä, osta paikan päältä', en: 'Browse online, buy in store' },
  { fi: 'Tue kivijalkaliikkeitä', en: 'Support local brick-and-mortar stores' },
  { fi: 'Vähemmän postitusta, enemmän paikallista kauppaa', en: 'Less shipping, more local commerce' },
]

export default function LandingScreen() {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()
  const { session } = useAuth()
  const { language } = useLanguage()

  useEffect(() => {
    if (session) navigation.reset({ index: 0, routes: [{ name: 'BuyerHome' }] })
  }, [session])

  // Animations
  const heroOpacity = useRef(new Animated.Value(0)).current
  const heroScale = useRef(new Animated.Value(0.9)).current
  const taglineOpacity = useRef(new Animated.Value(0)).current
  const dividerWidth = useRef(new Animated.Value(0)).current
  const propAnimations = VALUE_PROPS.map(() => ({
    opacity: useRef(new Animated.Value(0)).current,
    translateX: useRef(new Animated.Value(-30)).current,
  }))
  const ctaOpacity = useRef(new Animated.Value(0)).current
  const ctaTranslateY = useRef(new Animated.Value(20)).current

  useEffect(() => {
    Animated.parallel([
      Animated.timing(heroOpacity, { toValue: 1, duration: 800, useNativeDriver: true }),
      Animated.spring(heroScale, { toValue: 1, friction: 8, tension: 40, useNativeDriver: true }),
    ]).start()

    setTimeout(() => {
      Animated.timing(taglineOpacity, { toValue: 1, duration: 600, useNativeDriver: true }).start()
    }, 400)

    setTimeout(() => {
      Animated.timing(dividerWidth, { toValue: 1, duration: 500, useNativeDriver: false }).start()
    }, 700)

    propAnimations.forEach((anim, i) => {
      setTimeout(() => {
        Animated.parallel([
          Animated.timing(anim.opacity, { toValue: 1, duration: 500, useNativeDriver: true }),
          Animated.spring(anim.translateX, { toValue: 0, friction: 8, tension: 50, useNativeDriver: true }),
        ]).start()
      }, 900 + i * 200)
    })

    setTimeout(() => {
      Animated.parallel([
        Animated.timing(ctaOpacity, { toValue: 1, duration: 500, useNativeDriver: true }),
        Animated.timing(ctaTranslateY, { toValue: 0, duration: 500, useNativeDriver: true }),
      ]).start()
    }, 900 + VALUE_PROPS.length * 200)
  }, [])

  return (
    <YStack f={1} bg={theme.colors.background.primary}>
      {/* Language Toggle */}
      <XStack position="absolute" top={16} right={24} zIndex={10}>
        <LanguageSwitcher />
      </XStack>

      <ScrollView contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingBottom: 40 }} showsVerticalScrollIndicator={false}>
        {/* Hero */}
        <YStack ai="center" pt={Platform.OS === 'web' ? 80 : 100} pb={10} px="$xl">
          <Animated.View style={{ marginBottom: 8, opacity: heroOpacity, transform: [{ scale: heroScale }] }}>
            <Image source={fliisiLogo} style={{ width: 260, height: 95 }} resizeMode="contain" />
          </Animated.View>

          <Animated.Text style={{
            fontFamily: 'PlayfairDisplay_700Bold_Italic',
            fontSize: 22, color: theme.colors.text.secondary, textAlign: 'center',
            letterSpacing: 0.5, marginBottom: 24, opacity: taglineOpacity,
          }}>
            Second hand made easy
          </Animated.Text>

          <Animated.View style={{
            width: 60, height: 3, backgroundColor: theme.colors.brand.primary,
            borderRadius: 2, marginBottom: 10, transform: [{ scaleX: dividerWidth }],
          }} />
        </YStack>

        {/* Value Props */}
        <YStack px={28} py="$lg" maxWidth={768} w="100%" alignSelf="center">
          {VALUE_PROPS.map((prop, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <Animated.View style={{ alignItems: 'center', justifyContent: 'center', paddingVertical: 10, opacity: propAnimations[i].opacity }}>
                  <Flourish />
                </Animated.View>
              )}
              <Animated.View style={{ paddingVertical: 6, opacity: propAnimations[i].opacity, transform: [{ translateX: propAnimations[i].translateX }] }}>
                <Text
                  fontSize={17} fontWeight="500" fontStyle="italic" textAlign="center"
                  lineHeight={26} letterSpacing={0.2} color={theme.colors.text.primary}
                >
                  {language === 'fi' ? prop.fi : prop.en}
                </Text>
              </Animated.View>
            </React.Fragment>
          ))}
        </YStack>

        {/* CTA */}
        <Animated.View style={{ paddingHorizontal: 28, paddingTop: 20, paddingBottom: 30, maxWidth: 768, width: '100%', alignSelf: 'center', gap: 12, opacity: ctaOpacity, transform: [{ translateY: ctaTranslateY }] }}>
          <Button variant="primary" onPress={() => navigation.navigate('Login')}>
            <ButtonText>{language === 'fi' ? 'Kirjaudu sisään' : 'Sign in'}</ButtonText>
          </Button>

          <Button variant="outline" onPress={() => navigation.navigate('BuyerHome')}>
            <ButtonText variant="outline">{language === 'fi' ? 'Selaa tuotteita' : 'Browse products'}</ButtonText>
          </Button>

          <Text fontSize={13} color={theme.colors.text.secondary} fontStyle="italic" textAlign="center" mt="$sm" letterSpacing={0.3}>
            {language === 'fi' ? 'Selaa verkossa. Osta myymälästä.' : 'Browse online. Buy in store.'}
          </Text>
        </Animated.View>
      </ScrollView>
    </YStack>
  )
}
